import { inject, Injector, runInInjectionContext } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { MFEPackageIds } from '@interfaces/open-choice/mfe-details.enum';
import { Store } from '@ngrx/store';
import * as fromRootStore from '@store/index';
import {
  selectDocumentUploadMFEToggle,
  selectFeatureTogglesLoaded,
} from '@store/selectors/feature/feature.selectors';
import { combineLatest, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { mfeResolver } from './mfe.resolver';

export const wldOrLegacyDocUploadResolver: ResolveFn<void> = (route, state): Observable<void> => {
  const store: Store<fromRootStore.State> = inject(Store<fromRootStore.State>);
  const injector = inject(Injector);

  // Wait until the feature toggles have hydrated before snapshotting the WLD toggle.
  // On a cold browser refresh the toggle is `false` until the toggle API responds, so
  // a premature take(1) would silently load the legacy package and bypass the WLD path.
  // NOTE: we gate on `togglesLoaded` (not filter(Boolean) on the toggle itself) because
  // `false` is a legitimate, hydrated value for the legacy path.
  return combineLatest([
    store.select(selectFeatureTogglesLoaded),
    store.select(selectDocumentUploadMFEToggle),
  ]).pipe(
    filter(([togglesLoaded]) => togglesLoaded),
    take(1),
    switchMap(([, toggleOn]) => {
      const pkg = toggleOn ? MFEPackageIds.WLD_DOC_UPLOAD_PKG : MFEPackageIds.DOC_UPLOAD_PKG;
      store.dispatch(
        fromRootStore.LogInfo({
          payload: {
            eventId: 'wld-or-legacy-doc-upload-resolver',
            message: `WLD-or-legacy doc-upload resolver selected package ${pkg} (isWldDocUploadToggleOn=${toggleOn})`,
          },
        }),
      );
      const inner = mfeResolver(pkg);
      return runInInjectionContext(injector, () => inner(route, state) as Observable<void>);
    }),
  );
};
